import * as React from 'react';

import { Button, Modal, Stack } from 'react-bootstrap';
import { IReportParametersModel, IReportTypeModel } from '../../interfaces/Report/IReport';

import { IValidation } from '../../interfaces/IError';
import { LoadingButton } from '../../shared/buttons/LoadingButton';
import { cloneDeep } from 'lodash';
import { useZoneContext } from '../../contexts/useZoneContext';

export interface IExportModalProps {
    cancelbuttonText?: string;
    modalTitle?: string;
    onClose?: () => void;
    onError?: () => void;
    onExportCompleted?: () => void;
    open: boolean;
    reportParameters?: IReportParametersModel;
    reportType: IReportTypeModel;
    saveReportText?: string;
    submitButtonText?: string;
    url: string;
}

interface IExportModalState {
    showSettingsModal?: boolean;
    pendingResponse: boolean;
    validation: IValidation;
    saveReport?: boolean;
}

interface IStringBooleanMap {
    [key: string]: boolean;
}

export function ExportModal(props: IExportModalProps) {
    if (!props.open) return null;
    const { zoneGuid } = useZoneContext();

    const [state, setState] = React.useState<IExportModalState>({
        pendingResponse: false,
        validation: {},
    });
    const [validation, setValidation] = React.useState<IValidation>({});
    const [selectedCustomOptions, setSelectedCustomOptions] = React.useState<IStringBooleanMap>({});

    const generateAndDownloadReport = async () => {
        setState((prev) => ({ ...prev, pendingResponse: true }));
        setValidation({});

        const xhr = new XMLHttpRequest();
        xhr.open('POST', props.url, true);
        xhr.responseType = 'blob';
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('X-Tenant-Guid', zoneGuid);
        
        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
                let fileName = '';
                const blob = xhr.response;

                const contentDisposition = xhr.getResponseHeader('Content-Disposition');
                if (contentDisposition) {
                    const contentDispositionItems = contentDisposition.split(';');
                    if (contentDispositionItems) {
                        for (let i = 0; i < contentDispositionItems.length; i++) {
                            const currentItem = contentDispositionItems[i];
                            if (currentItem.includes('filename=')) {
                                const n = currentItem.indexOf('filename=') + 9;
                                fileName = contentDispositionItems[i].substring(
                                    n + 1,
                                    contentDispositionItems[i].length - 1
                                );
                                break;
                            }
                        }
                    }
                }
                const a = document.createElement('a');
                a.href = window.URL.createObjectURL(blob);
                a.download = fileName;
                a.dispatchEvent(new MouseEvent('click'));
                setState((prev) => ({
                    ...prev,
                    saveReport: false,
                    pendingResponse: false,
                    showSettingsModal: false,
                }));

                if (typeof props.onExportCompleted === 'function') {
                    props.onExportCompleted();
                }
            }

            if (xhr.readyState === XMLHttpRequest.DONE && xhr.status >= 400) {
                setState((prev) => ({
                    ...prev,
                    pendingResponse: false,
                }));
                setValidation({ export: ['Failed to export data'] });

                if (typeof props.onError === 'function') {
                    props.onError();
                }
            }
        };

        let reportParamsString = '';

        const reportParams: IReportParametersModel = cloneDeep(props.reportParameters) || {
            reportType: { id: props.reportType.id },
        };

        reportParams.saveReport = state.saveReport;

        if (reportParams.options) {
            reportParams.options = { ...reportParams.options, ...selectedCustomOptions };
        } else if (!reportParams.options) {
            reportParams.options = { ...selectedCustomOptions };
        }

        reportParamsString = JSON.stringify(reportParams);

        xhr.send(reportParamsString);
    };

    const handleCustomOptionChange = (key: string, checked: boolean) => {
        setSelectedCustomOptions((prev) => ({ ...prev, [key]: checked }));
    };

    return (
        <Modal
            centered
            show={props.open}
            onHide={() => {
                setState((prev) => ({ ...prev, showSettingsModal: false }));
            }}
            backdrop
        >
            <Modal.Header>
                <Modal.Title>{props.modalTitle ?? 'Export'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="container-fluid">
                    {Object.values(
                        props.reportType.availableReportOptions?.customOptions ?? {}
                    ).map((opt: { key: string; label: string }) => {
                        return (
                            <div>
                                <label>
                                    <Stack
                                        direction="horizontal"
                                        gap={2}
                                        style={{ alignItems: 'center' }}
                                    >
                                        <input
                                            checked={!!selectedCustomOptions[opt.key]}
                                            className="form-check-input"
                                            disabled={state.pendingResponse}
                                            name={'chk_' + opt.key}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleCustomOptionChange(opt.key, e.target.checked)
                                            }
                                            style={{ marginTop: 0 }}
                                            type="checkbox"
                                        />
                                        <span>{opt.label}</span>
                                    </Stack>
                                </label>
                            </div>
                        );
                    })}

                    <div>
                        <label>
                            <Stack direction="horizontal" gap={2} style={{ alignItems: 'center' }}>
                                <input
                                    checked={state.saveReport}
                                    className="form-check-input"
                                    disabled={state.pendingResponse}
                                    name="chkSaveReport"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setState((prev) => ({
                                            ...prev,
                                            saveReport: e.target.checked,
                                        }));
                                    }}
                                    style={{ marginTop: 0 }}
                                    type="checkbox"
                                />
                                <span>{props.saveReportText ?? 'Save Report'}</span>
                            </Stack>
                        </label>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Stack
                    direction="horizontal"
                    style={{ width: '100%', justifyContent: 'space-between' }}
                >
                    <div className="text-danger">{validation.export}</div>
                    <div>
                        <LoadingButton
                            onClick={generateAndDownloadReport}
                            disabled={state.pendingResponse}
                            loading={state.pendingResponse}
                            loadingText="Exporting"
                            text={props.submitButtonText ?? 'Run'}
                            variant="primary"
                        />
                        {!state.pendingResponse && (
                            <Button
                                variant="default"
                                onClick={() => {
                                    if (typeof props.onClose === 'function') {
                                        props.onClose();
                                    }
                                }}
                            >
                                {props.cancelbuttonText ?? 'Cancel'}
                            </Button>
                        )}
                    </div>
                </Stack>
            </Modal.Footer>
        </Modal>
    );
}
